import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth, formatApiError } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Logo } from '@/components/Logo';
import { AUTH } from '@/constants/testIds';

export default function RegisterPage() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const onSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      await register(email, password, name);
      navigate('/', { replace: true });
    } catch (err) {
      setError(formatApiError(err?.response?.data?.detail) || 'Registration failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen w-full grid grid-cols-1 lg:grid-cols-2 bg-white">
      <div
        className="hidden lg:block relative bg-cover bg-center"
        style={{
          backgroundImage:
            'linear-gradient(180deg, rgba(10,37,64,0.72) 0%, rgba(10,37,64,0.88) 100%), url(https://images.unsplash.com/photo-1512403754473-27835f7b9984?auto=format&fit=crop&w=1600&q=80)',
        }}
      >
        <div className="absolute inset-0 flex flex-col justify-between text-white">
          <Logo variant="wordmark" className="w-full h-auto block" alt="STEELPEAK" />
          <div className="p-14 pt-0">
            <h2 className="font-display text-4xl xl:text-5xl leading-tight max-w-md">
              Request access to the STEELPEAK knowledge network.
            </h2>
            <p className="mt-6 text-sm text-white/70 max-w-sm leading-relaxed">
              Team accounts unlock category-scoped intelligence across HR,
              Client Services, Wealth Advisory and Alternative Solutions.
            </p>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center px-6 py-16 lg:px-16 bg-[#FAFAFA]">
        <div className="w-full max-w-md">
          <div className="lg:hidden mb-10">
            <Logo variant="wordmark" className="h-7 w-auto rounded" alt="STEELPEAK" />
          </div>

          <div className="mb-10">
            <div className="text-xs uppercase tracking-[0.25em] text-[#D4AF37] mb-3">
              New Member
            </div>
            <h1 className="font-display text-4xl md:text-5xl text-[#0A2540] leading-tight">
              Create account
            </h1>
            <p className="mt-3 text-sm text-slate-500">
              Provisioned to the STEELPEAK RAG platform.
            </p>
          </div>

          <form onSubmit={onSubmit} data-testid={AUTH.registerForm} className="space-y-5">
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-wider text-slate-600">Full name</Label>
              <Input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                data-testid={AUTH.registerName}
                className="h-11 rounded-md border-slate-300 focus-visible:ring-[#0A2540]"
                placeholder="Jane Portfolio"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-wider text-slate-600">Email</Label>
              <Input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid={AUTH.registerEmail}
                className="h-11 rounded-md border-slate-300 focus-visible:ring-[#0A2540]"
                placeholder="you@spwm.com"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-wider text-slate-600">Password</Label>
              <Input
                type="password"
                required
                minLength={6}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid={AUTH.registerPassword}
                className="h-11 rounded-md border-slate-300 focus-visible:ring-[#0A2540]"
                placeholder="Minimum 6 characters"
              />
            </div>

            {error && (
              <div
                data-testid={AUTH.authError}
                className="text-sm text-red-600 border-l-2 border-red-500 pl-3 py-1"
              >
                {error}
              </div>
            )}

            <Button
              type="submit"
              disabled={submitting}
              data-testid={AUTH.registerSubmit}
              className="w-full h-11 bg-[#0A2540] hover:bg-slate-800 text-white rounded-md tracking-wide"
            >
              {submitting ? 'Creating…' : 'Create Account'}
            </Button>
          </form>

          <div className="mt-8 text-sm text-slate-500 text-center">
            Already have access?{' '}
            <Link
              to="/login"
              data-testid={AUTH.registerToLogin}
              className="text-[#0A2540] font-medium hover:text-[#D4AF37] underline underline-offset-4"
            >
              Sign in
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
